import React, { useContext, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import { BiData, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { default as AddCircleIcon } from '@wix/da-ds/pkg/Icons/AddCircle';
import IconButton from '../../../Button/IconButton';
import { useIsCommentEditorSubmittable } from '../../../CommentEditor/hooks/useIsCommentEditorSubmittable';
import { CommentEditorSource } from '../../../CommentEditor/redux/types';
import { CommentEditorContext, EditorContext } from '../../../CommentEditor/context';
import s from './CommentBoxFooter.scss';
const RESIZE_THROTTLE_DELAY = 300;
const RESIZE_THROTTLE_OPTIONS = {
    trailing: true
};
const COLLAPSE_BREAKPOINT_WIDTH = 440;
/**
 * The footer for our CommentBox editor.
 *
 * TODO: Refactor this more. There is room for this to be modularized.
 */ const CommentBoxFooter = ({ onCancel, onPost, className })=>{
    var _toolbars;
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    const { isBusy, isExpanded: isEditorExpanded, shouldUseFallback, isFallbackEmpty, editorSource } = useContext(CommentEditorContext);
    const isSubmittable = useIsCommentEditorSubmittable();
    const { toolbars } = useContext(EditorContext);
    const Toolbar = (_toolbars = toolbars) === null || _toolbars === void 0 ? void 0 : _toolbars.INSERT_PLUGIN;
    const isEdit = editorSource === CommentEditorSource.Edit;
    const [isToolbarCollapsable, setIsToolbarCollapsable] = useState(isMobile);
    const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
    const rootRef = useRef(null);
    const [rootWidth, setRootWidth] = useState(0);
    const handleResize = useThrottle(()=>{
        if (isMobile) {
            return;
        }
        const newWidth = rootRef.current.clientWidth;
        if (newWidth !== rootWidth) {
            setRootWidth(newWidth);
            setIsToolbarCollapsable(newWidth < COLLAPSE_BREAKPOINT_WIDTH);
        }
    }, [
        rootWidth,
        isMobile
    ], RESIZE_THROTTLE_DELAY, RESIZE_THROTTLE_OPTIONS);
    useResizeObserver(rootRef, handleResize);
    const handleToolbarClick = (ev)=>{
        setIsToolbarExpanded(false);
    };
    return /*#__PURE__*/ React.createElement("footer", {
        className: classnames(s['root'], (isEditorExpanded || shouldUseFallback) && s['editor-expanded'], isToolbarCollapsable && s['collapsable-toolbar'], isToolbarExpanded && s['expanded-toolbar'], className),
        ref: rootRef
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['toolbar-wrapper']
    }, !shouldUseFallback && Toolbar && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(IconButton, {
        className: s['toolbar-toggle'],
        onClick: ()=>setIsToolbarExpanded(!isToolbarExpanded),
        "aria-label": t('commentbox.a11y.button.expandToolbar')
    }, /*#__PURE__*/ React.createElement(AddCircleIcon, {
        size: IconSize.MEDIUM
    })), /*#__PURE__*/ React.createElement("div", {
        className: s['toolbar'],
        onClick: handleToolbarClick
    }, /*#__PURE__*/ React.createElement(Toolbar, {
        className: s['editor-toolbar']
    })))), /*#__PURE__*/ React.createElement("div", {
        className: s['right']
    }, /*#__PURE__*/ React.createElement(TextButton, {
        variant: "main",
        size: "small",
        disabled: isBusy || shouldUseFallback && isFallbackEmpty,
        onClick: onCancel,
        biData: BiData({
            evid: 189,
            itemid: BiContextProvidedValue,
            typeid: BiContextProvidedValue,
            is_comment_thread: BiContextProvidedValue,
            is_sticky: 0
        })
    }, t('commentbox.button.cancel')), /*#__PURE__*/ React.createElement(TextButton, {
        variant: "approval-green",
        size: "medium",
        disabled: !isSubmittable,
        onClick: onPost
    }, isEdit ? t('commentbox.button.update') : t('commentbox.button.comment'))));
};
export default CommentBoxFooter;
